import { useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrgHall } from "@/hooks/useOrgHall";
import { format, startOfDay, setHours, setMinutes, addMinutes } from "date-fns";
import { toast } from "sonner";
import { getLocalDayBounds } from "@/lib/utils";
import { calculateBookingPrice, type PricingRule } from "@/lib/pricing";
import { resolveMoney } from "@/utils/formatPrice";
import type {
  ScheduleBooking,
  CourtWithHall,
  CustomerWithCategory,
  ScheduleTrainingSession,
  BookingCategory,
  SelectionInfo,
} from "@/components/admin/schedule/types";

export function useScheduleGridData(selectedDate: Date) {
  const { user } = useAuth();
  const { organization, courts: orgCourts, hallId, hallHours, hall } = useOrgHall();
  const qc = useQueryClient();

  // FIX #2: Show ALL courts (not just active) — actually show all from orgCourts
  const courts = useMemo(
    () => (orgCourts as CourtWithHall[]).filter((c) => c.status !== "closed"),
    [orgCourts],
  );
  const courtIds = useMemo(() => courts.map((c) => c.id), [courts]);

  const { data: pricingRules = [] } = useQuery({
    queryKey: ["pricing-rules-admin", hallId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pricing_rules")
        .select("*")
        .eq("hall_id", hallId!);
      if (error) throw error;
      return (data ?? []) as PricingRule[];
    },
    enabled: !!hallId,
  });

  const { data: bookings = [] } = useQuery({
    queryKey: ["schedule-bookings", hallId, format(selectedDate, "yyyy-MM-dd")],
    queryFn: async () => {
      const { start, end } = getLocalDayBounds(selectedDate);
      // activity-click-fix: reverse-FK embed `linked_open_match` so the
      // grid can route clicks on activity-linked bookings to the new
      // ActivityDetailModal and render the activity title + organizer
      // first name instead of the generic player/price label.
      //
      // schedule-grid-gap-1-fix: parallel reverse-FK embed
      // `linked_league_match` for league-match-backfilled bookings
      // (PR #249). 98 such bookings org-wide; without this embed they
      // shadow the Seriespel overlay branch and render as "<admin> 0 kr"
      // routing to BookingDetailModal instead of MatchDetailModal.
      // Mirrors the PR #251 pattern.
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "*, courts!inner(name, hall_id), profiles:bookings_user_id_profiles_fkey(full_name, phone), customers(full_name, email), linked_open_match:activities!bookings_activity_id_fkey(id, activity_title, payment_mode, status, created_by), linked_league_match:league_matches!league_matches_booking_id_fkey(id, status, scheduled_date, scheduled_time, rescheduled_date, rescheduled_time, league_id, home_team_id, away_team_id, leagues(id, name), home_team:teams!league_matches_home_team_id_fkey(id, team_name), away_team:teams!league_matches_away_team_id_fkey(id, team_name))",
        )
        .eq("courts.hall_id", hallId!)
        .gte("start_time", start)
        .lte("start_time", end)
        .is("deleted_at", null)
        .order("start_time");
      if (error) throw error;
      return (data ?? []) as ScheduleBooking[];
    },
    enabled: !!hallId,
  });

  // Fetch customer categories for member detection
  const bookingUserIds = useMemo(
    () => [...new Set(bookings.map((b) => b.user_id))],
    [bookings],
  );
  const { data: customerCats = [] } = useQuery({
    queryKey: ["schedule-customer-cats", organization?.id, bookingUserIds],
    queryFn: async () => {
      if (bookingUserIds.length === 0) return [];
      const { data } = await supabase
        .from("customers")
        .select("user_id, category_id, customer_categories(slug, name)")
        .eq("organization_id", organization!.id)
        .in("user_id", bookingUserIds);
      return data ?? [];
    },
    enabled: !!organization?.id && bookingUserIds.length > 0,
  });

  const catByUser = useMemo(() => {
    const map = new Map<string, { slug: string; name: string } | null>();
    (customerCats as CustomerWithCategory[]).forEach((c) => {
      map.set(c.user_id!, c.customer_categories ?? null);
    });
    return map;
  }, [customerCats]);

  const { data: trainingSessions = [] } = useQuery({
    queryKey: ["schedule-training", hallId, format(selectedDate, "yyyy-MM-dd")],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_sessions")
        .select("*, training_groups(name, color), courts!inner(name, hall_id)")
        .eq("courts.hall_id", hallId!)
        .eq("session_date", format(selectedDate, "yyyy-MM-dd"))
        .neq("status", "cancelled");
      return data ?? [];
    },
    enabled: !!hallId,
  });

  // Fetch payments for color coding
  const bookingIds = useMemo(() => bookings.map((b) => b.id), [bookings]);
  const { data: allPayments = [] } = useQuery({
    queryKey: ["schedule-payments", bookingIds],
    queryFn: async () => {
      if (bookingIds.length === 0) return [];
      // Sprint D aggregates: include amount_minor + currency for dual-state read.
      const { data } = await supabase
        .from("booking_payments")
        .select("booking_id, status, amount, amount_minor, currency")
        .in("booking_id", bookingIds);
      return data ?? [];
    },
    enabled: bookingIds.length > 0,
  });

  // Fetch recurring bookings
  const dayOfWeek = selectedDate.getDay();
  const dateStr = format(selectedDate, "yyyy-MM-dd");
  const { data: recurringBookings = [] } = useQuery({
    queryKey: ["schedule-recurring", hallId, dateStr],
    queryFn: async () => {
      // Sprint D aggregates: include price_minor + currency for dual-state read.
      const { data } = await supabase
        .from("recurring_bookings")
        // S1 BUG-A fix (2026-05-19): organization_id behövs i SubscriptionDetailDialog
        // för subscription_activity_log INSERT (RLS WITH CHECK kräver det). Saknades
        // tidigare → audit-INSERT träffade RLS-policy som rejectade NULL-org_id.
        .select("id, organization_id, court_id, start_time, end_time, user_id, customer_id, price, price_minor, currency, interval_weeks, comment, payment_method, skipped_dates, valid_from, valid_until, day_of_week, status")
        .eq("day_of_week", dayOfWeek)
        .lte("valid_from", dateStr)
        .gte("valid_until", dateStr)
        .eq("status", "active");
      return data ?? [];
    },
    enabled: !!hallId,
  });

  // Fetch recurring blocks (named blocks like "Byggpadel")
  const { data: recurringBlocks = [] } = useQuery({
    queryKey: ["schedule-recurring-blocks", hallId, dateStr],
    queryFn: async () => {
      const { data } = await supabase
        .from("recurring_blocks" as any)
        .select("id, name, court_ids, start_time, end_time, color, customer_id, price, comment, skipped_dates, block_category, valid_from, valid_until, interval_weeks, estimated_value, is_confirmed_revenue, day_of_week")
        .eq("day_of_week", dayOfWeek)
        .lte("valid_from", dateStr)
        .eq("status", "active");
      // Filter: valid_until is null (indefinite) or >= dateStr
      return (data ?? []).filter((b: any) => !b.valid_until || b.valid_until >= dateStr);
    },
    enabled: !!hallId,
  });

  // PR-D Layer 1 (TASK-75) — Fetch league_matches placed on this hall + date.
  // Only matches with a scheduled_time (i.e. actually placed on the calendar)
  // count as visual blockers. Cancelled matches don't block. The DB trigger
  // (Layer 4) is the structural guarantee — this is for clean admin UX.
  //
  // Bug C fix (4-bugs-investigation §2) — when a match has been rescheduled,
  // `rescheduled_*` holds the new time; `scheduled_*` stay original. The grid
  // must render at the EFFECTIVE (rescheduled if set, else scheduled) date+
  // time, so:
  //   1. Server-side: SELECT both pairs + filter permissively via `.or()`
  //   2. Client-side: narrow to rows where COALESCE(rescheduled_*, scheduled_*)
  //      matches dateStr
  //   3. The returned `match.scheduled_time` is OVERWRITTEN with the effective
  //      time so downstream helpers (getLeagueMatchAt, getLeagueMatchSlotInfo,
  //      ScheduleGridCell) render at the right slot without any caller change.
  //      Same for scheduled_date.
  const { data: leagueMatches = [] } = useQuery({
    queryKey: ["schedule-league-matches", hallId, dateStr],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_matches")
        .select(
          "id, court_id, scheduled_date, scheduled_time, rescheduled_date, rescheduled_time, status, leagues!inner(id, match_duration_minutes, hall_id, name)",
        )
        // Permissive server-side: match if EITHER scheduled_date or
        // rescheduled_date is the requested day. Client-side narrows to
        // effective-date equality below.
        .or(`scheduled_date.eq.${dateStr},rescheduled_date.eq.${dateStr}`)
        .neq("status", "cancelled")
        .eq("leagues.hall_id", hallId!);
      if (error) throw error;
      type RawMatch = {
        id: string;
        court_id: string | null;
        scheduled_date: string | null;
        scheduled_time: string | null;
        rescheduled_date: string | null;
        rescheduled_time: string | null;
        status: string;
        leagues: { id: string; match_duration_minutes: number | null; hall_id: string; name: string } | null;
      };
      const raw = (data ?? []) as RawMatch[];
      // Narrow + normalize. The returned `scheduled_date` / `scheduled_time`
      // are the EFFECTIVE values — downstream code can keep using them as
      // before; the actual DB scheduled_* (when different) is preserved on
      // the raw object but consumers don't need it for grid rendering.
      const normalized = raw
        .map((m) => {
          const effDate = m.rescheduled_date ?? m.scheduled_date;
          const effTime = m.rescheduled_time ?? m.scheduled_time;
          return {
            ...m,
            // Effective values overwrite the originals on the in-memory shape.
            scheduled_date: effDate,
            scheduled_time: effTime,
          };
        })
        // Require an effective date + time match for the requested view day.
        .filter((m) => m.scheduled_date === dateStr && m.scheduled_time != null);
      return normalized as Array<{
        id: string;
        court_id: string | null;
        scheduled_date: string;
        scheduled_time: string;
        rescheduled_date: string | null;
        rescheduled_time: string | null;
        status: string;
        leagues: { id: string; match_duration_minutes: number | null; hall_id: string; name: string } | null;
      }>;
    },
    enabled: !!hallId,
  });

  // Fetch subscription releases for this date
  const { data: subscriptionReleases = [] } = useQuery({
    queryKey: ["schedule-sub-releases", hallId, dateStr],
    queryFn: async () => {
      const { data } = await supabase
        .from("subscription_releases" as any)
        .select("id, subscription_id, release_date, court_id, start_time, end_time, status, original_price, released_by, credit_amount, rebooked_booking_id")
        .eq("organization_id", organization!.id)
        .eq("release_date", dateStr)
        .in("status", ["released", "rebooked"]);
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  // Fetch customer names for recurring bookings (subscriptions)
  const rbCustomerIds = useMemo(
    () => [...new Set((recurringBookings as any[]).map((r) => r.customer_id).filter(Boolean))],
    [recurringBookings],
  );
  const { data: rbCustomers = [] } = useQuery({
    queryKey: ["rb-customers", rbCustomerIds],
    queryFn: async () => {
      if (rbCustomerIds.length === 0) return [];
      const { data } = await supabase.from("customers").select("id, full_name, email").in("id", rbCustomerIds);
      return data ?? [];
    },
    enabled: rbCustomerIds.length > 0,
  });
  const rbCustomerMap = useMemo(
    () => new Map((rbCustomers as any[]).map((c) => [c.id, c.full_name || c.email || "Kund"])),
    [rbCustomers],
  );

  // Lookup for recurring blocks by court+time
  const getRecurringBlockAt = useCallback(
    (courtId: string, slotStart: Date) => {
      const slotTime = format(slotStart, "HH:mm:ss");
      const slotEnd = format(addMinutes(slotStart, 30), "HH:mm:ss");
      for (const block of recurringBlocks as any[]) {
        if (!block.court_ids?.includes(courtId)) continue;
        if (slotTime >= block.end_time || slotEnd <= block.start_time) continue;
        // Check skipped dates
        if (block.skipped_dates?.includes(dateStr)) continue;
        return block;
      }
      return null;
    },
    [recurringBlocks, dateStr],
  );

  // Lookup for recurring bookings (subscriptions) by court+time
  const getRecurringBookingAt = useCallback(
    (courtId: string, slotStart: Date) => {
      const slotTime = format(slotStart, "HH:mm:ss");
      const slotEnd = format(addMinutes(slotStart, 30), "HH:mm:ss");
      for (const rb of recurringBookings as any[]) {
        if (rb.court_id !== courtId) continue;
        if (slotTime >= rb.end_time || slotEnd <= rb.start_time) continue;
        // Check skipped dates
        if (rb.skipped_dates?.includes(dateStr)) continue;
        // Check interval_weeks
        if (rb.interval_weeks && rb.interval_weeks > 1 && rb.valid_from) {
          const fromDate = new Date(rb.valid_from);
          const currentDate = new Date(dateStr);
          const diffWeeks = Math.round((currentDate.getTime() - fromDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
          if (diffWeeks % rb.interval_weeks !== 0) continue;
        }
        return rb;
      }
      return null;
    },
    [recurringBookings, dateStr],
  );

  // PR-D Layer 1 (TASK-75) — Look up the placed league_match (if any) that
  // covers the given (court, slotStart) cell. A 90-min match spans
  // 3 × 30-min cells; this returns the match for any cell inside that span.
  const getLeagueMatchAt = useCallback(
    (courtId: string, slotStart: Date) => {
      const slotMin = slotStart.getHours() * 60 + slotStart.getMinutes();
      for (const lm of leagueMatches) {
        if (lm.court_id !== courtId) continue;
        if (!lm.scheduled_time) continue;
        const [h, m] = lm.scheduled_time.split(":").map(Number);
        const startMin = h * 60 + m;
        const duration = lm.leagues?.match_duration_minutes ?? 90;
        const endMin = startMin + duration;
        // Slot covers [slotMin, slotMin+30). Match covers [startMin, endMin).
        // We want overlap (slotMin < endMin AND slotMin+30 > startMin).
        if (slotMin < endMin && slotMin + 30 > startMin) return lm;
      }
      return null;
    },
    [leagueMatches],
  );

  // Returns league match span info for rendering (mirrors getTrainingSlotInfo).
  const getLeagueMatchSlotInfo = useCallback(
    (courtId: string, slotStart: Date) => {
      const lm = getLeagueMatchAt(courtId, slotStart);
      if (!lm || !lm.scheduled_time) return null;
      const [h, m] = lm.scheduled_time.split(":").map(Number);
      const startMin = h * 60 + m;
      const slotMin = slotStart.getHours() * 60 + slotStart.getMinutes();
      const isFirst = slotMin === startMin;
      const duration = lm.leagues?.match_duration_minutes ?? 90;
      const slotCount = Math.max(1, Math.ceil(duration / 30));
      return { match: lm, isFirst, slotCount };
    },
    [getLeagueMatchAt],
  );

  // Lookup for subscription releases by court+time
  const getSubscriptionReleaseAt = useCallback(
    (courtId: string, slotStart: Date) => {
      const slotTime = format(slotStart, "HH:mm:ss");
      for (const rel of subscriptionReleases as any[]) {
        if (rel.court_id !== courtId) continue;
        if (slotTime < rel.start_time || slotTime >= rel.end_time) continue;
        return rel;
      }
      return null;
    },
    [subscriptionReleases],
  );

  const paymentsByBooking = useMemo(() => {
    const map: Record<string, { status: string; amount: number; amount_minor: number | null; currency: string }[]> = {};
    allPayments.forEach((p) => {
      if (!map[p.booking_id]) map[p.booking_id] = [];
      map[p.booking_id].push(p);
    });
    return map;
  }, [allPayments]);

  const recurringCourtTimes = useMemo(() => {
    return new Set(recurringBookings.map((r) => `${r.court_id}_${r.start_time}`));
  }, [recurringBookings]);

  const getBookingCategory = useCallback(
    (booking: ScheduleBooking): BookingCategory => {
      if (booking.status === "cancelled") return "cancelled";
      if (booking.status === "blocked") return "blocked";
      // Free bookings (with audit reason)
      if ((booking as any).free_reason) return "free";
      // Invoice bookings get distinct category
      const invoiceStatus = (booking as any).invoice_status as string | null;
      if (invoiceStatus === "paid") return "invoice_paid";
      if (invoiceStatus === "pending" || invoiceStatus === "invoiced") return "invoice_pending";
      const bStart = new Date(booking.start_time);
      const timeStr = format(bStart, "HH:mm:ss");
      if (recurringCourtTimes.has(`${booking.court_id}_${timeStr}`)) return "recurring";
      const profile = booking.profiles;
      if (!profile?.full_name && !profile?.phone) return "admin";

      const userCat = catByUser.get(booking.user_id);
      const isMember = !!userCat && userCat.slug !== "standard";

      const payments = paymentsByBooking[booking.id] ?? [];
      if (payments.length === 0) return isMember ? "member_unpaid" : "unpaid";
      // Sprint D aggregates: prefer amount_minor; both totals in öre, comparison is unit-consistent.
      const totalAmountMinor = payments.reduce(
        (s, p) => s + (resolveMoney(p.amount_minor, p.amount, p.currency)?.valueMinor ?? 0), 0);
      const paidAmountMinor = payments
        .filter((p) => ["paid", "manual"].includes(p.status))
        .reduce((s, p) => s + (resolveMoney(p.amount_minor, p.amount, p.currency)?.valueMinor ?? 0), 0);
      if (paidAmountMinor >= totalAmountMinor && totalAmountMinor > 0) return isMember ? "member_paid" : "paid";
      if (paidAmountMinor > 0) return "partial";
      if (totalAmountMinor === 0 && isMember) return "member_paid";
      return isMember ? "member_unpaid" : "unpaid";
    },
    [paymentsByBooking, recurringCourtTimes, catByUser],
  );

  // FIX #6: timeSlots starts at hallHours.start (opening_time)
  // FIX #7 (V2 Issue 2.1): widen the slot range to cover any bookings that
  // fall outside hall hours on the selected day. Without this, bookings
  // starting at or after closing_time (e.g. 23:00 when closing=23:00) — or
  // very-early-morning bookings from seed data / cross-midnight recurring
  // subscriptions — are silently dropped because no slot matches their
  // start_time. Keeps current behaviour when all bookings are within hours.
  const timeSlots = useMemo(() => {
    if (!hallHours) return [];
    const day = startOfDay(selectedDate);
    const dayStr = day.toDateString();
    let startHour = hallHours.start;
    let endHour = hallHours.end;
    for (const b of bookings) {
      const bStart = new Date(b.start_time);
      const bEnd = new Date(b.end_time);
      if (bStart.toDateString() === dayStr && bStart.getHours() < startHour) {
        startHour = bStart.getHours();
      }
      if (bEnd > day) {
        const sameDayEnd = bEnd.toDateString() === dayStr;
        const neededEnd = sameDayEnd
          ? Math.ceil((bEnd.getHours() * 60 + bEnd.getMinutes()) / 60)
          : 24;
        if (neededEnd > endHour) endHour = neededEnd;
      }
    }
    const arr: Date[] = [];
    for (let h = startHour; h < endHour; h++) {
      arr.push(setMinutes(setHours(day, h), 0));
      arr.push(setMinutes(setHours(day, h), 30));
    }
    return arr;
  }, [selectedDate, hallHours, bookings]);

  const getBookingAt = useCallback(
    (courtId: string, slotStart: Date) => {
      const slotEnd = addMinutes(slotStart, 30);
      return bookings.find((b) => {
        if (b.court_id !== courtId || b.status === "cancelled") return false;
        const bStart = new Date(b.start_time);
        const bEnd = new Date(b.end_time);
        return slotStart < bEnd && slotEnd > bStart;
      });
    },
    [bookings],
  );

  const getTrainingAt = useCallback(
    (courtId: string, slotStart: Date) => {
      const slotTotalMin = slotStart.getHours() * 60 + slotStart.getMinutes();
      return (trainingSessions as ScheduleTrainingSession[]).find((ts) => {
        if (ts.court_id !== courtId) return false;
        const [h, m] = (ts.start_time as string).split(":").map(Number);
        const startMin = h * 60 + m;
        const endMin = startMin + (ts.duration_minutes || 60);
        return slotTotalMin >= startMin && slotTotalMin < endMin;
      });
    },
    [trainingSessions],
  );

  const getTrainingSlotInfo = useCallback(
    (courtId: string, slotStart: Date) => {
      const ts = getTrainingAt(courtId, slotStart);
      if (!ts) return null;
      const tsTyped = ts as ScheduleTrainingSession;
      const [h, m] = (tsTyped.start_time as string).split(":").map(Number);
      const startMin = h * 60 + m;
      const slotMin = slotStart.getHours() * 60 + slotStart.getMinutes();
      const isFirst = slotMin === startMin;
      const slotCount = Math.ceil(tsTyped.duration_minutes / 30);
      return { training: ts, isFirst, slotCount };
    },
    [getTrainingAt],
  );

  const getPlayerName = useCallback((booking: ScheduleBooking) => {
    // Prefer customer name (direct FK), fall back to profile (auth user)
    const customer = (booking as any).customers;
    if (customer?.full_name) return customer.full_name;
    const profile = booking.profiles;
    if (profile?.full_name) return profile.full_name;
    return "Okänd";
  }, []);

  // activity-click-fix: normalize the linked_open_match embed. PostgREST
  // returns reverse-FK embeds as `{...}` for 1:1 relations or `[{...}]` for
  // 1:N — guard for both shapes. We only ever expect 0 or 1 open_match
  // per booking (open_matches.booking_id is unique by usage convention).
  const getLinkedOpenMatch = useCallback((booking: ScheduleBooking) => {
    const raw = (booking as any).linked_open_match;
    if (!raw) return null;
    if (Array.isArray(raw)) return raw[0] ?? null;
    return raw as { id: string; activity_title: string | null; payment_mode: string | null; status: string | null; created_by: string | null };
  }, []);

  // activity-click-fix: organizer user_ids from the linked open_matches.
  // Fetched as a batched profiles query so the grid can show the
  // organizer's first name on the booking block (e.g. "Sophie").
  const organizerUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const b of bookings) {
      const lom = getLinkedOpenMatch(b);
      if (lom?.created_by) ids.add(lom.created_by);
    }
    return Array.from(ids);
  }, [bookings, getLinkedOpenMatch]);

  const { data: organizerProfiles = [] } = useQuery({
    queryKey: ["schedule-organizer-profiles", hallId, organizerUserIds],
    queryFn: async () => {
      if (organizerUserIds.length === 0) return [];
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", organizerUserIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: organizerUserIds.length > 0,
  });

  const organizerNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of organizerProfiles as Array<{ id: string; full_name: string | null }>) {
      if (p.full_name) map.set(p.id, p.full_name);
    }
    return map;
  }, [organizerProfiles]);

  // activity-click-fix: label for activity-linked bookings.
  //   Line 1 = activity_title (truncated by the cell render)
  //   Line 2 = organizer first name (NOT price — activities are admin-
  //   driven; price_total=0 on the backfilled blocking booking is
  //   meaningless for the schedule grid label)
  const getActivityLabel = useCallback(
    (booking: ScheduleBooking): { title: string; organizerFirstName: string } | null => {
      const lom = getLinkedOpenMatch(booking);
      if (!lom) return null;
      const title = lom.activity_title?.trim() || "Aktivitet";
      const organizerFull = lom.created_by ? organizerNameById.get(lom.created_by) : null;
      const organizerFirstName = organizerFull ? organizerFull.split(/\s+/)[0] : "";
      return { title, organizerFirstName };
    },
    [getLinkedOpenMatch, organizerNameById],
  );

  // schedule-grid-gap-1-fix: normalize the linked_league_match embed
  // (PostgREST returns reverse-FK as `{...}` for 1:1 or `[{...}]` for 1:N).
  // Mirrors getLinkedOpenMatch.
  const getLinkedLeagueMatch = useCallback((booking: ScheduleBooking) => {
    const raw = (booking as any).linked_league_match;
    if (!raw) return null;
    if (Array.isArray(raw)) return raw[0] ?? null;
    return raw as {
      id: string;
      status: string | null;
      scheduled_date: string | null;
      scheduled_time: string | null;
      rescheduled_date: string | null;
      rescheduled_time: string | null;
      league_id: string | null;
      home_team_id: string | null;
      away_team_id: string | null;
      leagues: { id: string; name: string | null } | null;
      home_team: { id: string; team_name: string | null } | null;
      away_team: { id: string; team_name: string | null } | null;
    };
  }, []);

  // schedule-grid-gap-1-fix: label for league-match-linked bookings.
  //   Line 1 = "<home_team> vs <away_team>" (truncated by cell render)
  //   Line 2 = league name
  // Fallback chain (graceful degradation when team_name is null due to
  // data race or legacy rows):
  //   - both team names present  -> "<home> vs <away>"
  //   - one or both null         -> "Seriespel" + league name (line 2 unchanged)
  //   - league name null         -> "Seriespel" on line 2 (sentinel)
  const getLeagueMatchLabel = useCallback(
    (booking: ScheduleBooking): { teams: string; leagueName: string } | null => {
      const llm = getLinkedLeagueMatch(booking);
      if (!llm) return null;
      const home = llm.home_team?.team_name?.trim() ?? "";
      const away = llm.away_team?.team_name?.trim() ?? "";
      const teams = home && away ? `${home} vs ${away}` : "Seriespel";
      const leagueName = llm.leagues?.name?.trim() || "Seriespel";
      return { teams, leagueName };
    },
    [getLinkedLeagueMatch],
  );

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("bookings")
        .update({ status: "cancelled" })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule-bookings"] });
      toast.success("Bokning avbokad");
    },
  });

  // FIX #4: KPI calculations
  const kpis = useMemo(() => {
    const activeBookings = bookings.filter((b) => b.status !== "cancelled");
    const bookingCount = activeBookings.length;
    const rawTotalSlots = courts.length * timeSlots.length;

    // Calculate recurring block slots per category
    let revenueBlockSlots = 0;
    let operationalBlockSlots = 0;
    let confirmedBlockRevenue = 0;
    let estimatedBlockRevenue = 0;
    for (const block of recurringBlocks as any[]) {
      if (!block.start_time || !block.end_time) continue;
      const startParts = block.start_time.split(":").map(Number);
      const endParts = block.end_time.split(":").map(Number);
      const durationMin = (endParts[0] * 60 + (endParts[1] || 0)) - (startParts[0] * 60 + (startParts[1] || 0));
      const slots = Math.max(1, Math.ceil(durationMin / 30));
      const courtCount = (block.court_ids as string[] | undefined)?.length ?? 1;
      const blockSlots = slots * courtCount;
      const cat = (block.block_category as string) || "revenue";
      if (cat === "operational") {
        operationalBlockSlots += blockSlots;
      } else {
        revenueBlockSlots += blockSlots;
        const val = (block.estimated_value as number) || 0;
        if (block.is_confirmed_revenue) confirmedBlockRevenue += val;
        else estimatedBlockRevenue += val;
      }
    }

    // Calculate subscription (recurring_bookings) slots and revenue
    let subscriptionSlots = 0;
    let subscriptionRevenue = 0;
    let subscriptionCount = 0;
    for (const rb of recurringBookings as any[]) {
      if (!rb.start_time || !rb.end_time) continue;
      if (rb.skipped_dates?.includes(dateStr)) continue;
      if (rb.interval_weeks && rb.interval_weeks > 1 && rb.valid_from) {
        const fromDate = new Date(rb.valid_from);
        const currentDate = new Date(dateStr);
        const diffWeeks = Math.round((currentDate.getTime() - fromDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
        if (diffWeeks % rb.interval_weeks !== 0) continue;
      }
      const startParts = rb.start_time.split(":").map(Number);
      const endParts = rb.end_time.split(":").map(Number);
      const durationMin = (endParts[0] * 60 + (endParts[1] || 0)) - (startParts[0] * 60 + (startParts[1] || 0));
      const slots = Math.max(1, Math.ceil(durationMin / 30));
      subscriptionSlots += slots;
      // Sprint D aggregates: prefer price_minor.
      const subMoney = resolveMoney(
        (rb as any).price_minor,
        (rb as any).price,
        (rb as any).currency,
      );
      subscriptionRevenue += subMoney ? Math.round(subMoney.valueMinor / 100) : 0;
      subscriptionCount++;
    }

    // Operational blocks reduce available hours
    const totalSlots = rawTotalSlots - operationalBlockSlots;

    const bookedSlots = activeBookings.reduce((sum: number, b) => {
      return sum + Math.ceil((b.duration_minutes || 60) / 30);
    }, 0) + revenueBlockSlots + subscriptionSlots;

    const occupancy = totalSlots > 0 ? Math.round((bookedSlots / totalSlots) * 100) : 0;
    // Sprint D aggregates: prefer price_total_minor / standard_price_total_minor.
    const actualRevenueMinor = activeBookings.reduce(
      (sum: number, b) => sum + (resolveMoney((b as any).price_total_minor, b.price_total, (b as any).currency)?.valueMinor ?? 0),
      0,
    );
    const actualRevenue = Math.round(actualRevenueMinor / 100) + confirmedBlockRevenue + subscriptionRevenue;
    const standardRevenueMinor = activeBookings.reduce(
      (sum: number, b) => {
        const std = resolveMoney((b as any).standard_price_total_minor, (b as any).standard_price_total, (b as any).currency);
        const fallback = resolveMoney((b as any).price_total_minor, b.price_total, (b as any).currency);
        return sum + (std?.valueMinor ?? fallback?.valueMinor ?? 0);
      },
      0,
    );
    const standardRevenue = Math.round(standardRevenueMinor / 100);
    const freeHours =
      totalSlots > 0 ? Math.round((totalSlots - bookedSlots) * 0.5) : 0;
    return { bookingCount: bookingCount + subscriptionCount, occupancy, actualRevenue, standardRevenue, freeHours, estimatedBlockRevenue, confirmedBlockRevenue, subscriptionRevenue, subscriptionCount };
  }, [bookings, courts.length, timeSlots.length, recurringBlocks, recurringBookings, dateStr]);

  const computeSelectionInfo = useCallback(
    (
      selectionRect: {
        startCourtIdx: number;
        endCourtIdx: number;
        startSlotIdx: number;
        endSlotIdx: number;
      } | null,
    ): SelectionInfo | null => {
      if (!selectionRect) return null;
      const minC = Math.min(selectionRect.startCourtIdx, selectionRect.endCourtIdx);
      const maxC = Math.max(selectionRect.startCourtIdx, selectionRect.endCourtIdx);
      const minS = Math.min(selectionRect.startSlotIdx, selectionRect.endSlotIdx);
      const maxS = Math.max(selectionRect.startSlotIdx, selectionRect.endSlotIdx);
      const startTime = timeSlots[minS];
      const endTime = addMinutes(timeSlots[maxS], 30);
      if (!startTime || !endTime) return null;
      const durationMinutes = (maxS - minS + 1) * 30;
      const selectedCourts = courts.slice(minC, maxC + 1);
      const sCourtIds = selectedCourts.map((c) => c.id);
      const courtNames = selectedCourts.map((c) => c.name);
      const courtCount = selectedCourts.length;
      const courtLabel =
        courtCount === 1
          ? courtNames[0]
          : `${courtNames[0]}–${courtNames[courtNames.length - 1]}`;
      const perCourtPrice =
        hallId && pricingRules.length > 0
          ? calculateBookingPrice(pricingRules, hallId, startTime, durationMinutes)
          : null;
      const price = perCourtPrice
        ? { ...perCourtPrice, total: perCourtPrice.total * courtCount }
        : null;
      return {
        startTime,
        endTime,
        durationMinutes,
        courtLabel,
        courtNames,
        courtIds: sCourtIds,
        courtCount,
        price,
        primaryCourtId: sCourtIds[0],
      };
    },
    [timeSlots, courts, hallId, pricingRules],
  );

  const invalidateBookings = useCallback(() => {
    qc.refetchQueries({ queryKey: ["schedule-bookings"] });
    qc.refetchQueries({ queryKey: ["schedule-recurring-blocks"] });
    qc.refetchQueries({ queryKey: ["schedule-recurring"] });
    qc.refetchQueries({ queryKey: ["schedule-sub-releases"] });
    qc.refetchQueries({ queryKey: ["schedule-league-matches"] });
  }, [qc]);

  return {
    user,
    organization,
    hall,
    hallId,
    hallHours,
    courts,
    courtIds,
    bookings,
    trainingSessions: trainingSessions as ScheduleTrainingSession[],
    pricingRules,
    catByUser,
    timeSlots,
    kpis,
    getBookingCategory,
    getBookingAt,
    getTrainingAt,
    getTrainingSlotInfo,
    getPlayerName,
    // activity-click-fix exports
    getLinkedOpenMatch,
    getActivityLabel,
    // schedule-grid-gap-1-fix exports
    getLinkedLeagueMatch,
    getLeagueMatchLabel,
    cancelMutation,
    computeSelectionInfo,
    invalidateBookings,
    recurringBookings,
    recurringBlocks,
    getRecurringBlockAt,
    getRecurringBookingAt,
    getSubscriptionReleaseAt,
    subscriptionReleases,
    rbCustomerMap,
    allPayments,
    // PR-D Layer 1 (TASK-75)
    leagueMatches,
    getLeagueMatchAt,
    getLeagueMatchSlotInfo,
  };
}
